import { apiFetch } from './client'
import type { Client, ClientCreate, ClientListResponse, ClientUpdate } from '../types/clients'
import type { PatientListItem } from '../types/patients'

export async function listClients(params: {
  search?: string
  limit?: number
  offset?: number
}): Promise<ClientListResponse> {
  const qs = new URLSearchParams()
  if (params.search) qs.set('search', params.search)
  if (params.limit !== undefined) qs.set('limit', String(params.limit))
  if (params.offset !== undefined) qs.set('offset', String(params.offset))
  const res = await apiFetch(`/api/clients?${qs.toString()}`)
  if (!res.ok) throw new Error('Failed to load clients')
  return res.json() as Promise<ClientListResponse>
}

export async function getClient(id: number): Promise<Client & { patients: PatientListItem[] }> {
  const res = await apiFetch(`/api/clients/${id}`)
  if (res.status === 404) throw new Error('Client not found')
  if (!res.ok) throw new Error('Failed to load client')
  return res.json() as Promise<Client & { patients: PatientListItem[] }>
}

export async function createClient(data: ClientCreate): Promise<Client> {
  const res = await apiFetch('/api/clients', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (res.status === 409) throw new Error('A client with this email already exists')
  if (!res.ok) {
    const err = (await res.json()) as { detail?: unknown }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to create client')
  }
  return res.json() as Promise<Client>
}

export async function updateClient(id: number, data: ClientUpdate): Promise<Client> {
  const res = await apiFetch(`/api/clients/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  if (res.status === 409) throw new Error('A client with this email already exists')
  if (!res.ok) throw new Error('Failed to update client')
  return res.json() as Promise<Client>
}

export async function deleteClient(id: number): Promise<void> {
  const res = await apiFetch(`/api/clients/${id}`, { method: 'DELETE' })
  if (!res.ok) throw new Error('Failed to deactivate client')
}

export async function sendPortalInvite(id: number): Promise<void> {
  const res = await apiFetch(`/api/clients/${id}/portal-invite`, { method: 'POST' })
  if (res.status === 503) throw new Error('Could not send email. Please try again in a few minutes.')
  if (!res.ok) {
    const err = (await res.json()) as { detail?: unknown }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to send portal invite')
  }
}
